import { ChangeDetectorRef, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { AuthService, QandaService, UserService } from '@org/shared-services';
import { Answer, Question, QuestionDAO, User } from '@org/interfaces';
import { map, Observable, tap } from 'rxjs';


interface Reply {
  id: string;
  questionId: string;
  fromUser: string;
  userRole: 'GM' | 'TECH';
  text: string;
  date: Date;
}


@Component({
    selector: 'lib-q',
    imports: [CommonModule, FormsModule],
    templateUrl: './Q.component.html',
    styleUrl: './Q.component.scss',
    animations: [
        trigger('expandCollapse', [
            transition(':enter', [
                style({ height: '0', opacity: 0 }),
                animate('200ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ height: '0', opacity: 0 }))
            ])
        ])
    ]
})
export class QComponent {
  techUsers: User[] = []; // Will contain the list of TECH users
  selectedTechs: User[] = [];
  questions: Array<{text: string}> = [];

  activeTab: 'active' | 'add' | 'myQuestions' = 'active';
  activeQuestions: Question[] = [];
 

  isGM: boolean = false;
  isTech: boolean = false;
  isDeveloper: boolean = false;
 
  myQuestions: Question[] = [];
  expandedTechGroups: { [key: string]: boolean } = {};

  constructor(
    private qandaService: QandaService,
    private authService: AuthService,
    private userService: UserService,
    private cdr: ChangeDetectorRef
  ) {
    // Set initial tab based on role
    this.activeTab = this.isTech ? 'myQuestions' : 'active';
  }

  ngOnInit() {
   
    this.isGM =   this.authService.hasRole('gm');
    this.isTech = this.authService.hasRole('tech');
    this.isDeveloper = this.authService.hasRole('developer');
   
    this.activeTab = this.isTech ? 'myQuestions' : 'active';

    this.qandaService.getTenantTechs().subscribe(techs => {
      this.techUsers = techs;
      console.log("All Tech Users", this.techUsers);
    });

    this.loadQuestions().subscribe();

   
  }

  loadQuestions(): Observable<void> {
    if (this.isTech) {
      return this.qandaService.getTechUserQuestions(this.authService.currentUserValue?.id as string).pipe(
        tap(questions => {
          this.myQuestions = questions;
          console.log("My Active User Questions", this.myQuestions);
        }),
        map(() => void 0)
      );
    } else {
      return this.qandaService.getQuestions(this.authService.currentUserValue?.id as string).pipe(
        tap(questions => {
          this.activeQuestions = questions;
          console.log("My GM Created Questions", this.activeQuestions);
        }),
        map(() => void 0)
      );
    }
  }

  get groupedQuestions(): Map<string, Question[]> {
    const grouped = new Map<string, Question[]>();
    
    this.activeQuestions.forEach(question => {
      question.to.forEach(tech => {
        const key = tech.name;
        if (!grouped.has(key)) {
          grouped.set(key, []);
        }
        grouped.get(key)?.push(question);
      });
    });
    
    return grouped;
  }


  toggleTechSelection(tech: User) {
    const index = this.selectedTechs.findIndex(t => t._id === tech._id);
    if (index === -1) {
      this.selectedTechs.push(tech);
    } else {
      this.selectedTechs.splice(index, 1);
    }
  }

  toggleQuestion(question: Question) {
    question.isExpanded = !question.isExpanded;
  }

  toggleTechGroup(techName: string) {
    this.expandedTechGroups[techName] = !this.expandedTechGroups[techName];
  }

  isSelected(techId: string): boolean {
    return this.selectedTechs.some(tech => tech._id && tech._id === techId);
  }

  addQuestion() {
    this.questions.push({text: ''});
  }

  removeQuestion(index: number) {
    this.questions.splice(index, 1);
  }

  submitQuestions() {
    if (this.selectedTechs.length === 0) {
      // Show error message - no techs selected
      return;
    }
    
    // Filter out empty questions
    const validQuestions = this.questions.filter(q => q.text.trim().length > 0);
    
    if (validQuestions.length === 0) {
      // Show error message - no valid questions
      return;
    }

    // Format data according to QuestionDAO interface
    const questionData: QuestionDAO = {
      questions: validQuestions.filter(q => q.text).map(q => q.text as string),
      to: this.selectedTechs.map(tech => tech._id as string)
    };


    this.qandaService.addQuestion(questionData).subscribe({
      next: (response) => {
        console.log('Question added successfully:', response);
        this.questions = [];
        this.selectedTechs = [];

         // Reload the active questions
        this.loadQuestions().subscribe(() => {
          // Switch to active tab
          this.activeTab = 'active';
          // Ensure view is updated
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        console.error('Error adding question:', error);
      }
    });
    
  }

  submitAnswer(question: Question) {
    if (question.replyText?.trim()) {
      const isExpanded = question.isExpanded; // Store expansion state
      
      this.qandaService.addAnswer({
        questionId: question._id,
        text: question.replyText
      }).subscribe({
        next: (response: Answer) => {
          // Load fresh data
          this.loadQuestions().subscribe(() => {
            // After loading, find the question and restore its state
            const updatedQuestion = this.myQuestions.find(q => q._id === question._id);
            if (updatedQuestion) {
              updatedQuestion.isExpanded = isExpanded;
            }
          });
        },
        error: (error: any) => console.error('Error submitting answer:', error)
      });
    }
  }

  confirmDelete(question: Question) {
    if (confirm('Are you sure you want to delete this question?')) {
      this.qandaService.deleteQuestion(question._id as string).subscribe({
        next: () => {
          // Preserve expanded states
          const expandedStates = { ...this.expandedTechGroups };
          
          // Reload questions
          this.loadQuestions().subscribe(() => {
            // Restore expanded states
            this.expandedTechGroups = expandedStates;
          });
        },
        error: (error) => console.error('Error deleting question:', error)
      });
    }
  }


}
